import { useState, useEffect, useRef } from 'react';

/**
 * useCountUp — rAF count-up animation hook (DESIGN-SPEC §3.7).
 *
 * Parses a target string such as `"+1.900"`, `"824"`, `"29"` or `"95%"`,
 * preserving its prefix (`+`), thousands separator (`.`) and suffix (`%`),
 * then animates from 0 to the final value with requestAnimationFrame when
 * `inView` becomes true. Purely non-numeric strings (e.g. `"Ilimitados"`)
 * are returned verbatim — never animated.
 *
 * The consuming component MUST render the real final value as text first so
 * there is no layout shift (CLS) and the value is SEO-safe; this hook only
 * supplies a transient display string while the entrance plays.
 *
 * @param {string}  targetString             Value to display, e.g. "+1.900" / "95%" / "Ilimitados".
 * @param {Object}  [opts]
 * @param {boolean} [opts.inView=false]      Start the animation when this becomes true.
 * @param {number}  [opts.duration=1200]     Animation duration in milliseconds.
 * @returns {string} The current display string (formatted like `targetString`).
 *
 * Reduced-motion / !inView guard: when the user prefers reduced motion, the
 * value is non-numeric, or the element is not yet in view, the FINAL value is
 * returned immediately — no rAF loop is started.
 * Motion = native requestAnimationFrame only; zero extra dependency.
 */
export default function useCountUp(targetString, opts = {}) {
  const { inView = false, duration = 1200 } = opts;

  const safeTarget = targetString == null ? '' : String(targetString);

  // Parse "<prefix><number><suffix>" preserving the thousands separator.
  // Group 1: leading non-digit prefix (e.g. "+", "$", " ").
  // Group 2: the numeric body, digits optionally grouped by "." thousands.
  // Group 3: trailing non-digit suffix (e.g. "%", " €").
  const parsed = parseNumeric(safeTarget);

  // Detect reduced-motion synchronously so the very first paint is correct.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Non-numeric, reduced-motion, or not-yet-in-view → final value immediately.
  const startResolved = !parsed || prefersReducedMotion || !inView;

  const [display, setDisplay] = useState(startResolved ? safeTarget : parsed.prefix + format(0, parsed) + parsed.suffix);

  const frameRef = useRef(0);

  useEffect(() => {
    // Nothing to animate: keep the verbatim / final value in sync and bail.
    if (!parsed || prefersReducedMotion || !inView) {
      setDisplay(safeTarget);
      return undefined;
    }

    // Graceful fallback when rAF is unavailable: snap to the final value.
    if (typeof requestAnimationFrame === 'undefined') {
      setDisplay(safeTarget);
      return undefined;
    }

    let startTs = null;
    const { value } = parsed;

    const tick = (ts) => {
      if (startTs === null) startTs = ts;
      const elapsed = ts - startTs;
      const progress = duration > 0 ? Math.min(elapsed / duration, 1) : 1;
      const eased = easeOutCubic(progress);
      const current = Math.round(value * eased);

      setDisplay(parsed.prefix + format(current, parsed) + parsed.suffix);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        // Land exactly on the original string to guarantee pixel-perfect parity.
        setDisplay(safeTarget);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(frameRef.current);
      }
    };
    // safeTarget identifies the value; parsed is derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeTarget, inView, duration, prefersReducedMotion]);

  return display;
}

/**
 * Easing for a natural deceleration (fast start, soft landing).
 * @param {number} t Progress in [0, 1].
 * @returns {number}
 */
function easeOutCubic(t) {
  const p = 1 - t;
  return 1 - p * p * p;
}

/**
 * Parse a numeric target string, capturing prefix, suffix and number format.
 * Returns `null` for purely non-numeric strings (e.g. "Ilimitados").
 *
 * @param {string} str
 * @returns {{ prefix: string, suffix: string, value: number, grouped: boolean } | null}
 */
function parseNumeric(str) {
  // Number body: digits, with optional "." used as a thousands separator.
  const match = str.match(/^(\D*?)(\d[\d.]*)(\D*)$/);
  if (!match) return null;

  const prefix = match[1];
  const body = match[2];
  const suffix = match[3];

  // Strip thousands separators to obtain the integer value.
  const digits = body.replace(/\./g, '');
  if (!/^\d+$/.test(digits)) return null;

  const value = parseInt(digits, 10);
  if (!Number.isFinite(value)) return null;

  // Preserve grouping only when the original used "." separators.
  const grouped = body.indexOf('.') !== -1;

  return { prefix, suffix, value, grouped };
}

/**
 * Format an integer like the original target (re-applying "." thousands grouping).
 * @param {number} n
 * @param {{ grouped: boolean }} parsed
 * @returns {string}
 */
function format(n, parsed) {
  const digits = String(n);
  if (!parsed.grouped) return digits;
  // Insert "." every three digits from the right.
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}
